/** The browser privacy signals the privacy policy promises to honor
 *  (Section 7): with either on, our pages do not load analytics or session
 *  recording. One implementation for both apps' analytics gates. Client-safe
 *  and pure: callers pass `globalThis`, tests pass plain objects. */

type PrivacySignalGlobals = {
  navigator?: {
    doNotTrack?: unknown;
    msDoNotTrack?: unknown;
    globalPrivacyControl?: unknown;
  };
  doNotTrack?: unknown;
};

/** The values posthog-js's own `respect_dnt` check treats as on (1.375.0),
 *  so a gate built on this never lets through a browser the SDK would have
 *  opted out. Browsers send "1" (old Firefox "yes"); "0" and "unspecified"
 *  are off. */
const DNT_ON = new Set<unknown>(["1", "yes", "true", 1, true]);

/** True when the browser sends Do Not Track: `navigator.doNotTrack`, old
 *  Safari's `window.doNotTrack`, or old IE/Edge's `navigator.msDoNotTrack`. */
export function isDoNotTrackSignal(globals: PrivacySignalGlobals): boolean {
  return [
    globals.navigator?.doNotTrack,
    globals.navigator?.msDoNotTrack,
    globals.doNotTrack,
  ].some((value) => DNT_ON.has(value));
}

/** True when the browser sends Global Privacy Control, which CCPA treats as
 *  a valid opt-out. */
export function isGlobalPrivacyControlSignal(
  globals: PrivacySignalGlobals,
): boolean {
  return globals.navigator?.globalPrivacyControl === true;
}

/** True when a request's `Sec-GPC` header carries Global Privacy Control: the
 *  server-side twin of `isGlobalPrivacyControlSignal`. The spec defines
 *  exactly one value, "1". */
export function requestSendsGlobalPrivacyControl(headers: Headers): boolean {
  return headers.get("sec-gpc")?.trim() === "1";
}

/** True when a request's `DNT` header says Do Not Track: the server-side
 *  twin of `isDoNotTrackSignal`. Browsers that still send it send "1". */
export function requestSendsDoNotTrack(headers: Headers): boolean {
  return headers.get("dnt")?.trim() === "1";
}
